import { Box, ClipboardList, Users } from 'lucide-react';
import React from 'react';

import { DomainWithDetails } from '../../types';
import { useTranslation } from 'react-i18next';

interface DomainCardProps {
  domain: DomainWithDetails;
  onClick?: () => void;
  className?: string;
}

export const DomainCard: React.FC<DomainCardProps> = ({
  domain,
  onClick,
  className = '',
}) => {
  const { i18n, t } = useTranslation();

  const createdAtLabel =
    i18n.language === 'en'
      ? new Date(domain.created_at).toLocaleDateString('en-GB')
      : new Date(domain.created_at).toLocaleDateString('fr-FR');

  return (
    <div
      className={`bg-card rounded-lg shadow-sm border border-border p-6 hover:shadow-md transition-shadow cursor-pointer ${className}`}
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-card-foreground mb-1">
            {domain.name}
          </h3>
          {domain.description && (
            <p className="text-sm text-muted-foreground line-clamp-2">
              {domain.description}
            </p>
          )}
        </div>
        <div></div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <Users className="h-5 w-5 text-blue-500" />
          </div>
          <div className="ml-2">
            <p className="text-sm font-medium text-card-foreground">
              {domain.total_members || 0}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('domains.card.members')}
            </p>
          </div>
        </div>

        <div className="flex items-center">
          <div className="flex-shrink-0">
            <ClipboardList className="h-5 w-5 text-purple-500" />
          </div>
          <div className="ml-2">
            <p className="text-sm font-medium text-card-foreground">
              {domain.total_cadrages || 0}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('domains.card.cadrages')}
            </p>
          </div>
        </div>

        <div className="flex items-center">
          <div className="flex-shrink-0">
            <Box className="h-5 w-5 text-orange-500" />
          </div>
          <div className="ml-2">
            <p className="text-sm font-medium text-card-foreground">
              {domain.total_use_cases || 0}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('domains.card.useCases')}
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {t('domains.card.createdOn', { date: createdAtLabel })}
          </span>
          <span className="text-blue-600 hover:text-blue-700 font-medium">
            {t('domains.card.viewDetails')}
          </span>
        </div>
      </div>
    </div>
  );
};
